/**
 * MandantenStatusBoard — Berater-Sicht: Live-Status der Mandanten-Eingaben.
 *
 * Zeigt pro Wizard-Modul (Triage + 6 Dimensionen), ob Daten erfasst sind.
 * Plus den BWA-Upload-Status. Reine SELECT-Queries auf existierende Tabellen
 * — kein neues Backend nötig.
 *
 * Heuristik für „Status":
 *   - 'complete' wenn dimension_data.structured_data nicht leer ODER
 *     dimension_data.free_text gesetzt ODER (für resources) financial_dataset
 *     hat min. eine Kennzahl
 *   - 'in-progress' für jeden anderen Datenstand außer „komplett leer"
 *   - 'empty' wenn keine Zeile existiert
 *
 * Wir lesen NICHT die Wizard-Store-Daten des Mandanten — die liegen in
 * dessen localStorage und sind für uns unsichtbar. Quelle ist immer die DB.
 *
 * Wird nur sichtbar, wenn `hasActiveAccess` true ist (sonst macht das Board
 * keinen Sinn — der Mandant hat noch nicht angefangen).
 */

import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Circle, Clock, RefreshCw } from 'lucide-react';
import { de } from '../../lib/i18n/de';
import { getSupabaseClient } from '../../lib/supabase';
import { STEPS, type StepKey } from '../hub/dimensions';
import { LoadingRows } from '../ui/Skeleton';

interface MandantenStatusBoardProps {
  readonly caseId: string;
  /** Wenn false, wird das Board nicht gerendert (kein Mandanten-Zugang). */
  readonly hasActiveAccess: boolean;
}

interface ModuleStatus {
  readonly stepId: StepKey;
  readonly state: 'empty' | 'in-progress' | 'complete';
  readonly lastEditedAtIso: string | null;
}

interface BoardData {
  readonly modules: ReadonlyArray<ModuleStatus>;
  readonly bwaPath: string | null;
  readonly bwaUploadedAtIso: string | null;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: BoardData }
  | { kind: 'error'; message: string };

export function MandantenStatusBoard({ caseId, hasActiveAccess }: MandantenStatusBoardProps) {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });

  const refresh = useCallback(async () => {
    setLoad({ kind: 'loading' });
    try {
      const sb = getSupabaseClient();

      // dimension_data (alle Dimensionen) + financial_dataset parallel laden.
      // supabase-js v2 Type-Quirk: select('foo, bar') liefert `never[]` für
      // hand-gepflegte Database-Types. Wir narrowen auf die Runtime-reale
      // Shape (analog zu dem Pattern in supabaseInvitations.ts).
      type DimRow = {
        dimension: string;
        structured_data: Record<string, unknown> | null;
        free_text: string | null;
        updated_at: string | null;
      };
      type FinRow = {
        raw_bwa_storage_path: string | null;
        revenue_monthly_cent: number | null;
        costs_monthly_cent: number | null;
        hours_monthly: number | null;
        liquidity_cent: number | null;
        updated_at: string | null;
      };

      const [dimResult, finResult] = await Promise.all([
        sb
          .from('dimension_data')
          .select('dimension, structured_data, free_text, updated_at')
          .eq('case_id', caseId)
          .is('deleted_at', null),
        sb
          .from('financial_dataset')
          .select(
            'raw_bwa_storage_path, revenue_monthly_cent, costs_monthly_cent, hours_monthly, liquidity_cent, updated_at',
          )
          .eq('case_id', caseId)
          .is('deleted_at', null)
          .maybeSingle(),
      ]);

      if (dimResult.error) throw new Error(dimResult.error.message);
      // financial_dataset darf 0 Zeilen haben (kein Fehler)
      if (finResult.error && finResult.error.code !== 'PGRST116') {
        throw new Error(finResult.error.message);
      }

      const dimRows = (dimResult.data ?? []) as DimRow[];
      const fin = finResult.data as FinRow | null;

      // Triage hat KEINE eigene dimension_data-Zeile; wir nehmen
      // pragmatisch an: wenn irgendeine andere Dimension Daten hat, ist
      // der Mandant über die Triage hinweg (Triage ist der erste Step).
      // Sonst Status 'empty' für Triage. Das ist ok — wir wollen keine
      // false-positive „Triage abgeschlossen" anzeigen.
      const dimensionStatuses: ModuleStatus[] = STEPS.map((s) => {
        if (s.id === 'triage') {
          const anyOther = dimRows.length > 0;
          return {
            stepId: s.id,
            state: anyOther ? 'complete' : 'empty',
            lastEditedAtIso: anyOther
              ? dimRows.reduce<string | null>((latest, r) => {
                  if (typeof r.updated_at !== 'string') return latest;
                  if (!latest || r.updated_at > latest) return r.updated_at;
                  return latest;
                }, null)
              : null,
          };
        }
        if (s.id === 'resources') {
          // Resources ist Spezialfall: Daten liegen in financial_dataset,
          // nicht (ausschließlich) in dimension_data. Anders als die übrigen
          // Dimensionen hat resources keine `fields` — Vollständigkeit wird
          // deshalb über die Kern-Kennzahlen erkannt (analog zum Store, wo
          // `markDimensionComplete` erst greift, wenn Umsatz UND Kosten stehen).
          if (!fin) return { stepId: s.id, state: 'empty', lastEditedAtIso: null };
          const anyValue =
            fin.revenue_monthly_cent !== null ||
            fin.costs_monthly_cent !== null ||
            fin.hours_monthly !== null ||
            fin.liquidity_cent !== null ||
            fin.raw_bwa_storage_path !== null;
          // 'complete', sobald die beiden BWA-Kernwerte (Umsatz + Kosten)
          // erfasst sind — sonst 'in-progress', solange irgendetwas anfing.
          const coreComplete = fin.revenue_monthly_cent !== null && fin.costs_monthly_cent !== null;
          return {
            stepId: s.id,
            state: coreComplete ? 'complete' : anyValue ? 'in-progress' : 'empty',
            lastEditedAtIso: typeof fin.updated_at === 'string' ? fin.updated_at : null,
          };
        }
        const row = dimRows.find((r) => r.dimension === s.id);
        if (!row) return { stepId: s.id, state: 'empty', lastEditedAtIso: null };
        const structured = row.structured_data ?? {};
        const hasStructured =
          structured && typeof structured === 'object' && Object.keys(structured).length > 0;
        const hasText = typeof row.free_text === 'string' && row.free_text.trim().length > 0;
        return {
          stepId: s.id,
          state: hasStructured || hasText ? 'complete' : 'in-progress',
          lastEditedAtIso: typeof row.updated_at === 'string' ? row.updated_at : null,
        };
      });

      setLoad({
        kind: 'ready',
        data: {
          modules: dimensionStatuses,
          bwaPath: fin?.raw_bwa_storage_path ?? null,
          bwaUploadedAtIso: fin?.updated_at ?? null,
        },
      });
    } catch (err) {
      setLoad({
        kind: 'error',
        message: err instanceof Error ? err.message : de.mandantBoard.errorLoadFailed,
      });
    }
  }, [caseId]);

  useEffect(() => {
    if (!hasActiveAccess) return;
    void refresh();
  }, [hasActiveAccess, refresh]);

  if (!hasActiveAccess) return null;

  return (
    <section className="mb-10 rounded-sm border border-stone-200 bg-white">
      <div className="flex items-start justify-between gap-4 border-b border-stone-200 px-6 py-5 md:px-10">
        <div>
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {de.mandantBoard.sectionLabel}
          </p>
          <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
            {de.mandantBoard.sectionTitle}
          </h2>
          <p className="mt-2 max-w-prose font-serif text-sm italic text-stone-500">
            {de.mandantBoard.sectionIntro}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            void refresh();
          }}
          disabled={load.kind === 'loading'}
          className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-sm border border-stone-200 bg-white px-3 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 hover:border-stone-900 hover:text-stone-900 disabled:opacity-40"
          aria-label={de.mandantBoard.refreshButton}
        >
          <RefreshCw size={12} />
          {de.mandantBoard.refreshButton}
        </button>
      </div>

      <div className="px-6 py-6 md:px-10">
        {load.kind === 'loading' && <LoadingRows rows={3} label="Status wird geladen" />}
        {load.kind === 'error' && (
          <div className="rounded-sm border border-red-200 bg-red-50 px-4 py-3">
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-red-800">
              Fehler
            </p>
            <p className="mt-1 font-serif text-sm text-red-900">{load.message}</p>
          </div>
        )}

        {load.kind === 'ready' && (
          <>
            <ul className="divide-y divide-stone-200">
              {load.data.modules.map((mod) => {
                const step = STEPS.find((s) => s.id === mod.stepId);
                if (!step) return null;
                return (
                  <li key={mod.stepId} className="flex items-start gap-4 py-3 first:pt-0 last:pb-0">
                    <StatusIcon state={mod.state} />
                    <div className="flex-1 min-w-0">
                      <p className="font-sans text-[11px] font-medium uppercase tracking-widest text-stone-500">
                        Modul {step.nummer}
                      </p>
                      <p className="font-serif text-sm font-bold text-stone-900">{step.name}</p>
                    </div>
                    <p className="flex-shrink-0 text-right font-serif text-xs italic text-stone-500">
                      {mod.lastEditedAtIso
                        ? new Date(mod.lastEditedAtIso).toLocaleDateString('de-DE')
                        : '—'}
                    </p>
                  </li>
                );
              })}
            </ul>

            {/* BWA-Status separat */}
            <div className="mt-6 flex items-start gap-4 rounded-sm border border-stone-200 bg-stone-50 px-4 py-3">
              <StatusIcon state={load.data.bwaPath ? 'complete' : 'empty'} />
              <div className="flex-1">
                <p className="font-sans text-[11px] font-medium uppercase tracking-widest text-stone-500">
                  Anhang
                </p>
                <p className="font-serif text-sm font-bold text-stone-900">
                  {de.mandantBoard.bwaTitle}
                </p>
                <p className="mt-0.5 font-serif text-xs italic text-stone-500">
                  {load.data.bwaPath
                    ? de.mandantBoard.bwaUploaded.replace(
                        '{date}',
                        load.data.bwaUploadedAtIso
                          ? new Date(load.data.bwaUploadedAtIso).toLocaleDateString('de-DE')
                          : '—',
                      )
                    : de.mandantBoard.bwaMissing}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function StatusIcon({ state }: { state: 'empty' | 'in-progress' | 'complete' }) {
  if (state === 'complete') {
    return (
      <CheckCircle2
        size={18}
        className="mt-0.5 flex-shrink-0 text-emerald-700"
        aria-label={de.mandantBoard.statusComplete}
      />
    );
  }
  if (state === 'in-progress') {
    return (
      <Clock
        size={18}
        className="mt-0.5 flex-shrink-0 text-amber-600"
        aria-label={de.mandantBoard.statusInProgress}
      />
    );
  }
  return (
    <Circle
      size={18}
      className="mt-0.5 flex-shrink-0 text-stone-300"
      aria-label={de.mandantBoard.statusEmpty}
    />
  );
}
